import { PropertyPathWalker } from '../internal/PropertyPathWalker.js';
import { ModelSystemProperties } from '../internal/ModelSystemProperties.js';
import { oneOptionLookupRecordTypeGuard, multiPropertiesLookupRecordTypeGuard, singlePropertyLookupTypeGuard, extendedSinglePropertyLookupTypeGuard } from './ResolutionSchemaDefinition.js';
import { engineUtils } from '../services/EngineUtils.js';
import { TypeService } from '../services/TypeService.js';
import { ExplicitResolutionStrategy } from './ExplicitResolutionStrategy.js';
export class ResolutionSchema {
    constructor(lookups) {
        this.lookups = lookups;
    }
    topOptions(lookupId) {
        const result = new Set();
        const lookup = this.getLookupById(lookupId);
        if (!lookup) {
            return [];
        }
        if (oneOptionLookupRecordTypeGuard(lookup)) {
            const ids = lookup.fixed;
            for (const id of ids) {
                let fixedLookup = this.getLookupById(id);
                if (fixedLookup) {
                    result.add(fixedLookup.option);
                }
            }
        }
        else {
            throw new Error('Record type Not supported');
        }
        return Array.from(result);
    }
    variationOptions(lookupId) {
        const result = new Set();
        const lookup = this.getLookupById(lookupId);
        for (const variation of lookup.variation) {
            const lookupIds = Array.isArray(variation) ? variation : [variation];
            for (const id of lookupIds) {
                let bottomLookup = this.getLookupById(id);
                if (bottomLookup) {
                    result.add(bottomLookup.option);
                }
            }
        }
        return Array.from(result);
    }
    topFiltersFor(lookupId) {
        const optionProperties = new Map();
        for (const lookup of this.lookups) {
            if (oneOptionLookupRecordTypeGuard(lookup)) {
                if (lookup.id === lookupId) {
                    return this.createOptionFilters(optionProperties);
                }
                else {
                    if (!optionProperties.get(lookup.option)) {
                        optionProperties.set(lookup.option, []);
                    }
                    if (singlePropertyLookupTypeGuard(lookup)) {
                        optionProperties.get(lookup.option).push([lookup.property]);
                    }
                    else if (extendedSinglePropertyLookupTypeGuard(lookup)) {
                        const properties = [lookup.properties[lookup.key]];
                        optionProperties.get(lookup.option).push(properties);
                    }
                    else if (multiPropertiesLookupRecordTypeGuard(lookup)) {
                        optionProperties.get(lookup.option).push(Object.keys(lookup.properties));
                    }
                }
            }
            else {
                throw new Error('Record type Not supported');
            }
        }
        return [];
    }
    createOptionFilters(optionProperties) {
        const result = [];
        for (let [optionPath, propertyNames] of optionProperties) {
            result.push(new OptionFilter(optionPath, propertyNames));
        }
        return result;
    }
    allOptions() {
        const paths = new Set();
        for (const lookup of this.lookups) {
            if (oneOptionLookupRecordTypeGuard(lookup)) {
                paths.add(lookup.option);
            }
            else {
                throw new Error('Record type Not supported');
            }
        }
        return Array.from(paths);
    }
    getReference(lookupId, value, model, optionsToUse) {
        const map = new Map();
        const lookup = this.safelyGetLookupById(lookupId);
        const visited = new Set();
        for (const variation of lookup.variation) {
            const lookupIds = !Array.isArray(variation) ? [variation] : variation;
            for (const id of lookupIds) {
                if (visited.has(id)) {
                    continue;
                }
                const variantLookup = this.safelyGetLookupById(id);
                visited.add(id);
                const option = variantLookup.option;
                if (!optionsToUse.has(option)) {
                    continue;
                }
                let properties = map.get(option);
                if (!properties) {
                    properties = {};
                    map.set(option, properties);
                }
                if (singlePropertyLookupTypeGuard(variantLookup)) {
                    properties[variantLookup.property] = engineUtils.getPropertyPathValue(model, TypeService.joinPath(variantLookup.option, variantLookup.property));
                }
                else if (extendedSinglePropertyLookupTypeGuard(variantLookup)) {
                    const property = variantLookup.properties[variantLookup.key];
                    properties[property] = engineUtils.getPropertyPathValue(model, TypeService.joinPath(variantLookup.option, property));
                }
                else {
                    throw new Error('Not supported');
                }
            }
        }
        let properties = map.get(lookup.option);
        if (!properties) {
            properties = {};
            map.set(lookup.option, properties);
        }
        if (singlePropertyLookupTypeGuard(lookup)) {
            properties[lookup.property] = value;
        }
        else if (extendedSinglePropertyLookupTypeGuard(lookup)) {
            const property = lookup.properties[lookup.key];
            properties[property] = value[lookup.key];
        }
        else {
            throw new Error('Not supported');
        }
        return map;
    }
    orderOptionValues(lookupId, model, lookupValue, optionsPotentialValues) {
        const lookup = this.getLookupById(lookupId);
        const options = new Set(optionsPotentialValues.keys());
        const reference = this.getReference(lookupId, lookupValue, model, options);
        const strategy = new ExplicitResolutionStrategy(lookup, this, reference);
        return strategy.orderOptionValues(model, optionsPotentialValues);
    }
    getAllOptionResolutionProperties(optionPath) {
        const result = [];
        for (let record of this.lookups) {
            if (singlePropertyLookupTypeGuard(record)) {
                if (record.option === optionPath) {
                    result.push(record.property);
                }
            }
            else if (extendedSinglePropertyLookupTypeGuard(record)) {
                if (record.option === optionPath) {
                    result.push(record.properties[record.key]);
                }
            }
            else if (multiPropertiesLookupRecordTypeGuard(record)) {
                if (record.option === optionPath) {
                    result.push(...Object.values(record.properties));
                }
            }
        }
        return result;
    }
    getLookupById(lookupId) {
        for (const record of this.lookups) {
            if (record.id === lookupId) {
                if (oneOptionLookupRecordTypeGuard(record)) {
                    return record;
                }
            }
        }
        return undefined;
    }
    safelyGetLookupById(id) {
        const lookup = this.getLookupById(id);
        if (!lookup) {
            throw new Error(`Cannot find lookup with id ${id}`);
        }
        return lookup;
    }
    getVariationLookups(variation) {
        const result = [];
        for (const variant of variation) {
            const ids = Array.isArray(variant) ? variant : [variant];
            for (const id of ids) {
                result.push(this.safelyGetLookupById(id));
            }
        }
        return result;
    }
}
export function order(options, properties, model, path) {
    let nextToOrdered = 0;
    while (properties.length) {
        const property = properties.shift();
        const propertyPath = TypeService.joinPath(path, property);
        const value = engineUtils.getPropertyPathValue(model, propertyPath);
        for (let i = nextToOrdered; i < options.length; i++) {
            const optionValue = engineUtils.getPropertyPathValue(options[i].model, property);
            if (engineUtils.equals(optionValue, value)) {
                const tmp = options[nextToOrdered];
                options[nextToOrdered] = options[i];
                options[i] = tmp;
                nextToOrdered++;
            }
        }
    }
}
export class OptionFilter {
    constructor(optionPropertyPath, properties) {
        this.optionPropertyPath = optionPropertyPath;
        this.properties = properties;
    }
    match(schema, model, option) {
        return this.properties.every(properties => {
            const typeRef = ModelSystemProperties.getTypeRef(model);
            const value = new PropertyPathWalker(this.optionPropertyPath, schema, model, typeRef).getPropertyValue().value;
            const left = engineUtils.getPropertyPathValues(value, properties);
            const right = engineUtils.getPropertyPathValues(option.model, properties);
            return engineUtils.equals(left, right);
        });
    }
}
//# sourceMappingURL=ResolutionSchema.js.map